import React, { useState, memo, ChangeEvent } from 'react'
import { useDispatch } from 'react-redux'
import { Select } from 'components/forms/Selects'
import { Autocomplete } from 'components/forms/autocomplete/DPLAutocomplete'
import AutocompleteBase, {
  TAutocompleteBaseChangeHandler,
  TInputChangeHandler,
  getAutocompleteOptions,
} from 'transfix-ui/components/AutocompleteBase'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'
import { IconButton } from 'transfix-ui/components/IconButton'
import SuggestLocation from 'transfix-ui/components/SuggestLocation'
import {
  ILocation,
  IEquipmentType,
  IAwardType,
  IShipperRatesFilterState,
} from 'apps/rms/types/shipperRates'
import { fetchShipperRates } from 'apps/rms/utils/actions/shipperRates'
import { TextInput } from 'components/atoms'
import { SearchIcon } from 'transfix-icons'

const truckTypes = [
  {
    value: 0,
    label: 'Any Equipment',
  },
  {
    value: 3,
    label: 'Dry Van',
  },
  {
    value: 2,
    label: 'Reefer',
  },
  {
    value: 1,
    label: 'Flatbed',
  },
]

const pricingPrograms = [
  {
    groupIndex: 1,
    id: 'Standard',
    optionType: 'default',
    text: 'Standard',
  },
  {
    groupIndex: 1,
    id: 'Dynamic Contract Rate',
    optionType: 'default',
    text: 'Dynamic Contract Rate',
  },
  {
    groupIndex: 1,
    id: 'TrueRate+',
    optionType: 'default',
    text: 'TrueRate+',
  },
]

const awardTypes = [
  {
    value: '',
    label: 'Any Award Type',
  },
  {
    value: 'primary',
    label: 'Primary',
  },
  {
    value: 'backup',
    label: 'Backup',
  },
  {
    value: 'published',
    label: 'Published',
  },
]

const initialFormState = {
  equipmentId: 0,
  pricingProgram: '',
} as IShipperRatesFilterState

interface IShipperRatesFilter {
  activeStatus?: string
}

export const ShipperRatesFilter = memo<IShipperRatesFilter>(({ activeStatus }) => {
  const dispatch = useDispatch()
  const [state, setState] = useState<IShipperRatesFilterState>(initialFormState)
  const setFilterState = (change: {}) =>
    setState((current: IShipperRatesFilterState) => {
      const nextState = { ...current, ...change }
      return nextState
    })
  const setLocation = (key: string, value: ILocation) => {
    const location = {
      [`${key}City`]: value.city,
      [`${key}StateCode`]: value.state,
      [`${key}PostalCode`]: value.postalCode,
    }
    setFilterState(location)
  }
  const setPickup = (value: ILocation) => setLocation('pickup', value)
  const setDelivery = (value: ILocation) => setLocation('delivery', value)

  const setAccountManager = ({
    fullSelection,
    value,
  }: {
    value: number
    fullSelection?: { name: string }
  }) =>
    setFilterState({
      opsRepName: fullSelection?.name || undefined,
      opsRepId: value || undefined,
    })
  const setNewShipper = ({
    fullSelection,
    value,
  }: {
    value: number
    fullSelection?: { name: string }
  }) =>
    setFilterState({
      shipperName: fullSelection?.name || undefined,
      shipperApplicationId: value || undefined,
    })
  const setEquipmentKey = (equipment: IEquipmentType) =>
    setFilterState({ equipmentId: equipment.value })
  const setAwardType = (awardType: IAwardType) => setFilterState({ awardType: awardType.value })
  const shipperLaneIdChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    setFilterState({ shipperLaneId: value || undefined })
  const shipperRateIdChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
    setFilterState({ shipperRateId: value || undefined })
  const handleSubmit = () => {
    dispatch(
      fetchShipperRates({
        pageCount: 0,
        perPage: 15,
        filters: {
          ...state,
          activeStatus,
        },
      })
    )
  }

  const pricingProgramAutocompleteOptions = getAutocompleteOptions({
    groupIndex: 1,
    groupName: 'Pricing Programs',
    options: pricingPrograms,
    mapOption: ({ id = '', text = '' }) => ({ text, id }),
  })

  const programChangeHandler: TAutocompleteBaseChangeHandler = (e, newVal) => {
    if (isAutocompleteBaseOption(newVal)) {
      const setProgram = newVal.text || ''
      setFilterState({
        pricingProgram: setProgram,
      })
    }
  }

  const programInputChangeHandler: TInputChangeHandler = (e, newVal) => {
    if (e) {
      setFilterState({
        pricingProgram: newVal,
      })
    }
  }

  const pickup = {
    city: state.pickupCity,
    state: state.pickupStateCode,
    postalCode: state.pickupPostalCode,
  }
  const delivery = {
    city: state.deliveryCity,
    state: state.deliveryStateCode,
    postalCode: state.deliveryPostalCode,
  }

  return (
    <div className='row'>
      <div className='col-xs-12 col-md-2 col-lg-1'>
        <Autocomplete
          label='AM'
          placeholder='Enter account manager'
          value={state.opsRepName}
          onChange={setAccountManager}
          type='user'
          id='shipper-rates-account-manager'
          dataTest='account-manager'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-2'>
        <Autocomplete
          label='Shipper'
          placeholder='Enter shipper name'
          value={state.shipperName}
          onChange={setNewShipper}
          type='shipper'
          id='shipper-rates-shipper'
          dataTest='shipper-name'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-2'>
        <TextInput
          label='Lane ID'
          value={state.shipperLaneId || ''}
          onChange={shipperLaneIdChangeHandler}
          placeholder='Lane ID'
          id='shipper-rates-shipper-lane-id'
          dataTest='shipper-lane-id'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-2'>
        <SuggestLocation
          label='Pickup Location'
          placeholder='City or State'
          countries={['US', 'CA']}
          value={pickup}
          onChange={setPickup}
          id='shipper-rates-pickup'
          dataTest='pickup-location'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-2'>
        <SuggestLocation
          label='Delivery Location'
          placeholder='City or State'
          countries={['US', 'CA']}
          value={delivery}
          onChange={setDelivery}
          id='shipper-rates-delivery'
          dataTest='delivery-location'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-1'>
        <Select
          options={truckTypes}
          value={state.equipmentId}
          onChange={setEquipmentKey}
          label='Equipment'
          placeholder='Select equipment type'
          id='shipper-rates-equipment'
          dataTest='equipment-type'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-1'>
        <TextInput
          label='Award ID'
          value={state.shipperRateId || ''}
          onChange={shipperRateIdChangeHandler}
          placeholder='Award ID'
          id='shipper-rates-id'
          dataTest='shipper-rate-id'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-1'>
        <Select
          options={awardTypes}
          value={state.awardType}
          onChange={setAwardType}
          label='Award Type'
          placeholder='Select award type'
          id='shipper-rates-award'
          dataTest='award-type'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-2'>
        <AutocompleteBase
          label='Pricing Program'
          options={pricingProgramAutocompleteOptions}
          placeholder='Select Program'
          onChange={programChangeHandler}
          inputValue={state.pricingProgram}
          onInputChange={programInputChangeHandler}
          id='shipper-rates-pricing-program'
        />
      </div>
      <div className='col-xs-12 col-md-2 col-lg-1 mt4 pt1'>
        <IconButton
          Icon={SearchIcon}
          color='secondary'
          onClick={handleSubmit}
          id='shipper-rates-filter'
          data-test='submit-button'
          label='Search'
          size='large'
        />
      </div>
    </div>
  )
})
